// A fixed type drawn by the site's own component (`field_components` of the hook): a site with its own select, its own upload box
// replaces how the renderer draws that type and keeps everything else - the value, the conditions, the checks, the errors.
// The component draws everything of the field (label, control, error); it gets:
//   field, value, onChange, error (text), disabled, required, id, styles, form_values, lang, messages   as a custom field does, and
//   options    select, multi_select, radio, checkbox, toggle: the field's options [{ title, value }]; state: the states; city: the cities
//              of the state chosen in its group (all of them when the group has no state field) - the same shape, [{ title, value }]
//   loading    state and city: the lists are not loaded yet
//   on_file    file: gives the chosen File to the renderer (type and size checked, read to base64, set as the value or shown as an error);
//              on_file(file, { accept }) also refuses a file outside the site's own narrower accept text (a logo input: images only)
//   accept, max_file_bytes   file: what the renderer accepts
//   text, on_text(text), input, unit   a typed field (text, textarea, number, price, mobile, email, national_code, postal_code): `text` is what the box
//              shows (a price with its separators), `on_text` takes what was typed and sets the value (a price becomes a number), `input` is how the
//              box is typed into { type, input_mode, dir, autocomplete, max_length, rows, placeholder, max_digits }, `unit` the text after a
//              price (price_unit); max_digits: the field takes digits only and at most this many (codes, a digits-only pattern), else null
// onChange takes what the renderer keeps for that type: an option object { title, value } (a list of them for multi_select and
// checkbox), the file value, text, ... (the same as for the built-in component).
import { h } from "../h.js";
import { GRID_KEYS, grid_of, DEFAULT_MAX_FILE_BYTES, TEXT_FIELD_KINDS } from "../constants.js";
import { LIMITS } from "../form_helper.js";
import { display_value, value_from_input } from "./textual.js";
import { cities_of_state } from "./geo.js";
import { field_accept, handle_file_chosen } from "./file.js";
import { digits_limit } from "../site_helpers.js";

export const options_for_site = ({ field, geo, state_value, all_cities }) => {
    if (field.type === "state") {
        return (geo?.states || []).map((item) => ({ title: item.title, value: item.value }));
    }
    if (field.type === "city") {
        const list = all_cities ? (geo?.cities || []) : cities_of_state(geo, state_value);
        return list.map((item) => ({ title: item.title, value: item.value }));
    }
    return Array.isArray(field.options) ? field.options : [];
};

export const SiteField = (props) => {
    const {
        site_component: Site, field, id, value, on_change, on_error, error, required, disabled, styles, class_names, lang, messages, form_values,
        geo, state_value, all_cities, max_file_bytes = DEFAULT_MAX_FILE_BYTES, price_unit,
    } = props;
    const extra = {};
    if (field.type === "file") {
        // the formats this field takes (the admin's choice, else every one); a site may narrow them further with on_file(file, { accept })
        const own_accept = field_accept(field);
        extra.on_file = (file, options) => handle_file_chosen(file, { on_change, on_error: on_error || (() => {}), max_bytes: max_file_bytes, accept: options?.accept, field_accept: own_accept });
        extra.accept = own_accept;
        extra.max_file_bytes = max_file_bytes;
    } else if (field.type === "textarea" || TEXT_FIELD_KINDS[field.type]) {
        const kind = TEXT_FIELD_KINDS[field.type] || TEXT_FIELD_KINDS.text;
        extra.text = display_value(field.type, value);
        extra.on_text = (text) => on_change(value_from_input(field.type, text));
        extra.input = {
            type: kind.input_type,
            input_mode: kind.input_mode,
            dir: field.direction || kind.dir,
            autocomplete: kind.autocomplete,
            max_length: field.type === "text" ? LIMITS.TEXT_MAX : (field.type === "textarea" ? LIMITS.TEXTAREA_MAX : undefined),
            rows: field.type === "textarea" ? (Number.isInteger(field.data?.rows) ? field.data.rows : 4) : undefined,
            placeholder: field.placeholder || undefined,
            max_digits: digits_limit(field),
        };
        extra.unit = field.type === "price" && typeof price_unit === "string" && price_unit !== "" ? price_unit : undefined;
    } else {
        extra.options = options_for_site({ field, geo, state_value, all_cities });
        extra.loading = (field.type === "state" || field.type === "city") && !geo;
    }
    return h(
        "div",
        { className: class_names?.[GRID_KEYS[grid_of(field)]], "data-field-type": field.type, "data-field-key": field.key },
        h(Site, { field, value, onChange: on_change, error, disabled, required, id, styles, form_values, lang, messages, ...extra }),
    );
};
